import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { authFetch } from "../utils/authFetch";

const ESCLUSE = new Set(["CONTENITORI", "OGGETTISTICA"]);

export default function AppAgenteNuovoOrdinePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const azienda = location.state?.azienda || null;

  const [prodotti, setProdotti] = useState([]);
  const [quantita, setQuantita] = useState({});
  const [ricerca, setRicerca] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [ordineCreatoId, setOrdineCreatoId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const response = await authFetch(
          "/api/prodotti/?scope=catalogo&tipo_listino=azienda"
        );
        const data = await response.json().catch(() => []);
        if (!response.ok) {
          throw new Error(data?.detail || "Errore caricamento catalogo.");
        }
        if (!cancelled) {
          setProdotti(
            (Array.isArray(data) ? data : []).filter(
              (p) => !ESCLUSE.has(String(p.category || "").trim().toUpperCase())
            )
          );
        }
      } catch (err) {
        if (!cancelled) setError(err.message || "Catalogo non disponibile.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (azienda?.id) load();
    else setLoading(false);

    return () => { cancelled = true; };
  }, [azienda?.id]);

  const filtrati = useMemo(() => {
    const q = ricerca.trim().toLowerCase();
    return prodotti.filter((p) =>
      !q ||
      String(p.name || "").toLowerCase().includes(q) ||
      String(p.category || "").toLowerCase().includes(q)
    );
  }, [prodotti, ricerca]);

  const righe = useMemo(
    () => prodotti
      .filter((p) => Number(quantita[p.id]) > 0)
      .map((p) => ({
        product: p.id,
        quantity: Number(quantita[p.id]),
        name: p.name,
      })),
    [prodotti, quantita]
  );

  const cambiaQuantita = (id, valore) => {
    const n = Number(valore);
    if (!Number.isInteger(n) || n < 0 || n > 9999) return;
    setQuantita((old) => ({ ...old, [id]: n }));
  };

  const salvaBozza = async () => {
    if (!azienda?.id || saving || ordineCreatoId) return;
    if (!righe.length) {
      setError("Seleziona almeno un prodotto.");
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");

    try {
      const response = await authFetch("/api/ordini/miei/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          azienda_id: Number(azienda.id),
          status: "DRAFT",
          payment_method: "RITIRO",
          per_nome: azienda.name,
          items: righe.map(({ product, quantity }) => ({ product, quantity })),
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          typeof data?.detail === "string"
            ? data.detail
            : JSON.stringify(data) || "Impossibile creare la bozza."
        );
      }

      if (!data?.id || data.status !== "DRAFT") {
        throw new Error(
          "Risposta inattesa dal server: verifica la bozza prima di riprovare."
        );
      }

      setOrdineCreatoId(data.id);
      setMessage(`Bozza ordine n. ${data.id} creata correttamente.`);
      navigate(`/app/agente/ordini/${data.id}/riepilogo`, { replace: true });
    } catch (err) {
      setError(err.message || "Errore durante il salvataggio.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container py-4" style={{ maxWidth: 760 }}>
      <button
        type="button"
        className="btn btn-outline-secondary rounded-pill mb-3"
        onClick={() => navigate(-1)}
      >
        Torna alla scheda azienda
      </button>

      <h2 className="fw-bold mb-3">Nuovo ordine aziendale</h2>

      {!azienda?.id ? (
        <div className="alert alert-warning">
          Nessuna azienda selezionata. Torna al portafoglio aziende.
        </div>
      ) : (
        <>
          <div className="card rounded-4 shadow-sm mb-3">
            <div className="card-body">
              <div className="small text-muted">Cliente selezionato</div>
              <h4 className="fw-bold mb-1">{azienda.name}</h4>
              <div className="text-muted">{azienda.code || ""}</div>
            </div>
          </div>

          <div className="alert alert-info">
            L'ordine sarà salvato come bozza. La firma del cliente sarà
            necessaria prima dell'invio.
          </div>

          {error && <div className="alert alert-danger">{error}</div>}
          {message && (
            <div className="alert alert-success">
              {message}
              {ordineCreatoId && (
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm ms-2"
                  onClick={() => navigate(`/app/agente/ordini/${ordineCreatoId}/riepilogo`)}
                >
                  Apri riepilogo
                </button>
              )}
            </div>
          )}

          <input
            type="search"
            className="form-control mb-3"
            placeholder="Cerca prodotto o categoria..."
            value={ricerca}
            onChange={(e) => setRicerca(e.target.value)}
          />

          {loading ? (
            <p>Caricamento catalogo...</p>
          ) : (
            <div className="d-flex flex-column gap-2 mb-4">
              {filtrati.map((p) => (
                <div key={p.id} className="border rounded-4 p-3">
                  <div className="fw-semibold">{p.name}</div>
                  <div className="small text-muted mb-2">
                    {p.category || "Prodotto"}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() =>
                        cambiaQuantita(p.id, Math.max(0, Number(quantita[p.id] || 0) - 1))
                      }
                    >
                      −
                    </button>
                    <span className="fw-bold" style={{ minWidth: 35, textAlign: "center" }}>
                      {quantita[p.id] || 0}
                    </span>
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() =>
                        cambiaQuantita(p.id, Number(quantita[p.id] || 0) + 1)
                      }
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
              {!filtrati.length && <p className="text-muted">Nessun prodotto trovato.</p>}
            </div>
          )}

          <div className="card rounded-4 shadow-sm mb-4">
            <div className="card-body">
              <h5 className="fw-bold">Riepilogo prodotti</h5>
              {righe.length ? (
                righe.map((r) => (
                  <div key={r.product} className="d-flex justify-content-between gap-3 mb-2">
                    <span>{r.name}</span>
                    <strong>× {r.quantity}</strong>
                  </div>
                ))
              ) : (
                <p className="text-muted mb-0">Nessun prodotto selezionato.</p>
              )}
            </div>
          </div>

          <button
            type="button"
            className="btn btn-success w-100 rounded-pill py-3 fw-bold mb-4"
            disabled={saving || loading || !righe.length || Boolean(ordineCreatoId)}
            onClick={salvaBozza}
          >
            {saving ? "Salvataggio..." : ordineCreatoId ? "Bozza già creata" : "Salva ordine come bozza"}
          </button>
        </>
      )}
    </div>
  );
}
